import { ICustomerCaseData } from 'types/customer-case';

import WhiteSensorsImage from 'assets/white-sensor.svg';
import WhiteAutomationIcon from 'assets/white-automation.svg';
import WhiteAirportIcon from 'assets/industries/airport.svg';
import { colors } from 'styles';
import Typography from '@mui/material/Typography/Typography';

import BlueShipImage from 'assets/customer-cases/port-operations/blue-cars.svg';

import CityViewImage from 'assets/customer-cases/port-operations/city-view.webp';
import BoatInPortImage from 'assets/customer-cases/port-operations/cars-in-port.webp';
import BoatWithLinesImage from 'assets/customer-cases/port-operations/containers.webp';
import BridgeImage from 'assets/customer-cases/port-operations/bridge.webp';
import HeroImage from 'assets/customer-cases/port-operations/hero.webp';

import WhiteSensorIcon from 'assets/white-sensor.svg';
import StretchToolIcon from 'assets/customer-cases/stretch-tool.svg';
import WhiteAgileIcon from 'assets/white-agile.svg';
import WhiteSimulationIcon from 'assets/customer-cases/simulation.svg';

import WhiteCircleAroundIcon from 'assets/white-circle-around.svg';
import WhiteDirectionRightIcon from 'assets/industries/direction-right.svg';
import { reinforcementLearningApplicationMeta } from 'constants/pages';

export const fifthCustomerCaseData: ICustomerCaseData = {
  heroData: {
    heroTitle: 'Advancing Autonomous Port Operations with Simulation',
heroDescription:
  `Learn how AILiveSim and Gim Robotics worked with the industry to develop solutions for autonomous port operations.\nBy combining GIM Robotics’ proven autonomous perception focus technologies with AILiveSim’s intelligent simulation and synthetic data platform, the teams enabled rapid validation of multi-machine operations in realistic, data-rich digital ports.\n\n**The result:** faster algorithm development, reduced on-site testing, and a scalable foundation for future automation programs.\n• **70% reduction** in on-site testing time \n• **55-75** % reduction in prototyping and data collection costs \n• **3× faster** validation of traffic and crane logic`,
    heroListTitle: 'Transforming Port Automation with Intelligent Simulation',
    buttonLabel: 'Explore Maritime Industry',
    buttonLink: '/Industries/maritime',
    heroListItems: [
      {
        description:
          'A configurable framework enabling realistic testing of mixed autonomous fleets — from cranes to AGVs and trucks.',
        Icon: WhiteSensorsImage,
        title: 'Multi-Machine Traffic Simulation',
      },
      {
        description:
          'Realistic crane models with sensor placement tools for validating perception and automation logic. ',
        Icon: WhiteAutomationIcon,
        title: 'High-Fidelity Crane Simulation',
      },
      {
        description:
          'Data-rich virtual ports supporting scenario creation, stress testing, and rare-event simulation.',
        Icon: WhiteAirportIcon,
        title: 'Digital Port Environments',
      },
    ],
    heroImages: {
      firstImage: {
        imageSrc: CityViewImage,
        altText: 'Port overview with container terminals',
      },
      secondImage: {
        imageSrc: BoatInPortImage,
        altText: 'Vessel at port terminal',
      },
      thirdImage: {
        imageSrc: BoatWithLinesImage,
        altText: 'Container handling operations',
      },
      fourthImage: {
        imageSrc: BridgeImage,
        altText: 'Port infrastructure',
      },
    },
  },
  customerCase: {
    title: 'The Challenge: Validating Autonomous Port Operations at Scale',
    description: (
      <>
        <Typography fontSize="18px" color={colors.mainDark}>
Modern port automation depends on precise coordination between autonomous vehicles, cranes, and safety systems operating in dynamic, high-risk environments. Before adopting simulation, validation relied heavily on physical testing to verify new traffic behaviors and control algorithms.
        </Typography>
        <br/>
      </>
    ),
    bottomText:
      'Traditional field testing was costly, time-consuming, and difficult to reproduce — particularly when validating rare events or new rule configurations. The team needed a scalable way to accelerate iteration while minimizing operational risks and expenses.',
    image: BlueShipImage,
    items: [
      'Safely testing and validating new autonomous control logic',
      'Prototyping and evaluating multi-machine interactions',
      'Reducing time and cost of repetitive on-site testing',
    ],
    link: '',
  },
  firstInformationBlockData: {
    title: 'The Solution: Bridging Real-World Autonomy and Simulation',
    description: `The collaboration between AILiveSim and GIM Robotics brought together advanced fleet-management expertise and a next-generation simulation platform — enabling rapid, scalable validation of autonomous port operations.`,
    items: [
      {
        Icon: WhiteAgileIcon,
        description: 'Evaluate mixed autonomous fleets: including cranes, AGVs, trucks, and straddle carriers, in realistic, high-complexity port conditions.',
        title: 'Traffic Simulation Framework',
      },
      {
        Icon: WhiteSensorIcon,
        description: 'Test and refine perception setups for crane automation with realistic sensor placement tools and high-fidelity digital models.',
        title: 'Crane & Perception Validation',
      },
      {
        Icon: StretchToolIcon,
        description: 'Decouple automation development from hardware by integrating a digital twin, enabling faster iteration and safe testing of full workflows.',
        title: 'Digital Twin–Driven Development',
      },
    ],
  },
  resultsData: {
    title: 'Results: Faster Validation and a Scalable Automation Workflow',
    description: (
      <>
        <Typography fontSize="18px" color={colors.mainDark} marginBottom="24px">
          The partnership between AILiveSim and GIM Robotics bridges the gap between real-world autonomy and simulation.
        </Typography>
        <ul style={{ margin: '0 0 24px 0', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '16px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            <b>GIM Robotics</b> main contribution was the top-of-the-line perception solution for vehicle detection, tracking and classification. Additionally, GIM contributed to the fleet management solution with its deep expertise in navigation related issues.
          </li>
          <li style={{ fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            <b>AILiveSim</b> provided an AI-driven simulation platform with realistic physics, traffic modeling, and automated scenario generation.
          </li>
        </ul>
        <Typography fontSize="18px" color={colors.mainDark} marginBottom="24px" style={{ whiteSpace: 'pre-line' }}>
          {`Together, the teams created a virtual testbed replicating full port operations — enabling engineers to test, iterate, and validate complex behaviors before deployment.
By integrating a digital twin of the machine with the simulator, automation development could be fully decoupled from physical hardware. This allows design verification entirely in simulation, where multiple design options can be iterated and validated in a short time — without additional commissioning costs. Development teams no longer need to wait for hardware commissioning or data collection before starting algorithm work, resulting in massive time savings.
The simulation environment also makes it possible to test complete use cases — including rare or critical corner cases that would be difficult, costly, or unsafe to reproduce in real life. As new features are developed, predefined test cases enable automatic regression testing without the burden of manual validation, ensuring consistent quality and cost-efficient continuous development.`}
        </Typography>
        <Typography fontSize="18px" color={colors.mainDark} fontWeight={700} marginBottom="16px">
          AILiveSim delivered:
        </Typography>
        <ul style={{ margin: '0 0 24px 0', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            A configurable traffic simulation framework for mixed autonomous fleets
          </li>
          <li style={{ marginBottom: '8px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            A realistic crane model for testing perception for crane automation, with an easy sensor placement tool
          </li>
          <li style={{ marginBottom: '8px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            A generic straddle carrier, a truck and AGV models that can be manually controlled or scripted to move on predefined paths
          </li>
          <li style={{ fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            A tailored training and support program on scenario creation, sensor setup, and test automation
          </li>
        </ul>
        <Typography fontSize="18px" color={colors.mainDark} marginBottom="24px">
          The integrated system was deployed in <b>just 8 weeks</b>, enabling continuous testing and development cycles that drastically shortened time-to-validation.
        </Typography>
        <Typography fontSize="18px" color={colors.mainDark} fontWeight={700} marginBottom="16px">
          Products & Technologies Used:
        </Typography>
        <ul style={{ margin: '0 0 24px 0', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            AILiveSim Simulation Platform – for scenario creation, system testing, and performance analysis
          </li>
          <li style={{ marginBottom: '8px', fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            Synthetic Data Engine – for perception algorithm training and validation
          </li>
          <li style={{ fontSize: '18px', color: colors.mainDark, lineHeight: '150%' }}>
            GIM Robotics perception software for vehicle detection, tracking and classification
          </li>
        </ul>
        <Typography fontSize="18px" color={colors.mainDark}>
          Joint Training & Support Program – enabling customer teams to scale internal expertise in simulation and autonomy
        </Typography>
      </>
    ),
    clientFeedbackMessage: `"The combination of GIM Robotics' control and perception expertise and AILiveSim's intelligent simulation has completely transformed our validation process. We can now test complex multi-machine interactions virtually, achieving in hours what once took days — with greater accuracy and safety."`,
    clientLabel: '— Program Manager, Leading maritime automation provider',
    listTitle: 'Key Outcomes',
    listItems: [
      <li key="1" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <b>Time Savings:</b> Validation cycles reduced from weeks to days via automated test execution.
      </li>,
      <li key="2" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <b>Cost Efficiency:</b> Simulation replaced repetitive field trials, reducing data acquisition costs by 70%.
      </li>,
      <li key="3" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <b>Productivity:</b> Engineering teams focus on improving control algorithms rather than managing physical test setups.
      </li>,
      <li key="4" style={{ lineHeight: '140%', fontSize: '16px' }}>
        <b>Scalability:</b> The joint solution supports new vehicle types, additional sites, and future automation programs.
      </li>,
    ],
    resultTitle: 'Cut Field Testing Costs with Simulation-First Development',
    resultDescription:
      'Simulation drastically reduced the dependency on constant physical testing, lowering operational expenses and enabling rapid, repeatable iteration across complex port scenarios. Teams now focus on improving autonomy software rather than managing field logistics.',
    secondClientFeedbackMessage: `"Before AILiveSim, development was slowed by costly data collection and manual labeling. Now, 80% of our training data is generated synthetically — including rare corner cases — enabling a working prototype in just two months instead of six. Data acquisition costs dropped by over 50%, and projects are delivered 2–4 months faster."`,
    secondClientLabel: '— Tommi Tikkanen, Lead Robotics Engineer at GIM Robotics',
    rightColumnLayout: true,
  },
  secondInformationBlockData: {
    title: 'Unlocking the Future of Autonomous Port Operations',
    description: `The automation teams now plan to expand their use of AILiveSim to accelerate development across additional port sites, introduce new vehicle types, and automate broader workflows — all supported by scalable, scenario-driven validation.`,
    items: [
      {
        Icon: WhiteCircleAroundIcon,
        description:
          'Plans to fully automate simulation workflows for faster, continuous validation across evolving port operations.',
        title: 'Automated Testing Pipelines',
      },
      {
        Icon: StretchToolIcon,
        description:
          'Vision to extend simulation-driven development to multiple terminals and future automation programs.',
        title: 'Scalable Port Deployment',
      },
      {
        Icon: WhiteDirectionRightIcon,
        description:
          'Broaden the scope of testing to include new vehicle behaviors, crane logic, and complex multi-agent coordination.',
        title: 'Expanded Autonomy Use Cases',
      },
      {
        Icon: WhiteSimulationIcon,
        description:
          'From digital twins to sensor fidelity, AILiveSim ensures every test mirrors real-world port conditions with precision.',
        title: 'Real-World Accuracy in Every Scenario',
      },
    ],
  },
  applicationData: {
    bottomText: '',
    description:
      'Training control algorithms in the real world is slow, risky, and costly, especially for complex maneuvers and rare events. AILiveSim enables scalable, simulation-in-the-loop reinforcement learning, where intelligent agents can practice thousands of scenarios safely, improve decision-making, and adapt to diverse operational challenges without real-world risk.',
    image: HeroImage,
    items: [],
    title: 'Learn More About Reinforcement Learning for Autonomous Machine Control',
    link: reinforcementLearningApplicationMeta.url,
  },
};
